import { useApi } from "@/lib/useApi";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Stat, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { fPct } from "@/lib/format";
import { LayoutDashboard, AlertOctagon, AlertTriangle, CheckCircle2, Shuffle, Clock, Sparkles, X, Layers, ListChecks, Wallet, Circle, ArrowRight } from "lucide-react";
import { flatNavForUser } from "@/components/AppShell";

function PinnedShortcuts() {
  const { user, effectiveRole } = useAuth();
  const pinned = user?.menu_prefs?.pinned || [];
  if (!pinned.length) return null;
  const byTo = Object.fromEntries(flatNavForUser(user, effectiveRole).map((n) => [n.to, n]));
  const picks = pinned.map((to) => byTo[to]).filter(Boolean);
  if (!picks.length) return null;
  return (
    <Card className="mt-4 p-4" data-testid="pinned-shortcuts">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-accent" />Mina genvägar</div>
      <div className="flex flex-wrap gap-2">
        {picks.map((n) => {
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={`pin-${n.to.slice(1)}`}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-secondary">
              {Icon && <Icon className="h-4 w-4 text-accent" />}{n.label}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function Bar({ dist }) {
  const { t } = useLang();
  const total = (dist?.latt || 0) + (dist?.medel || 0) + (dist?.tung || 0) || 1;
  const seg = [
    { k: t("load_latt"), v: dist?.latt || 0, c: "bg-sky-500" },
    { k: t("load_medel"), v: dist?.medel || 0, c: "bg-amber-500" },
    { k: t("load_tung"), v: dist?.tung || 0, c: "bg-orange-700" },
  ];
  return (
    <Card className="p-4">
      <div className="mb-3 text-sm font-semibold">{t("load_dist_title")}</div>
      <div className="flex h-4 overflow-hidden rounded-full bg-secondary">
        {seg.map((s) => <div key={s.k} className={s.c} style={{ width: `${(s.v / total) * 100}%` }} title={`${s.k}: ${Math.round((s.v / total) * 100)}%`} />)}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs">
        {seg.map((s) => (
          <span key={s.k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded-sm ${s.c}`} />{s.k} · {Math.round((s.v / total) * 100)}%</span>
        ))}
      </div>
    </Card>
  );
}

function AiSummary({ employeeId, onClose }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.post("/analysis/summary", { employee_id: employeeId })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [employeeId]);
  return (
    <Card className="border-accent/40 bg-accent/5 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-accent" />
        <span className="text-sm font-semibold">{t("ai_summary")} ({data?.generated_by === "ai" ? t("by_ai") : t("by_rules")})</span>
        <Button variant="ghost" size="icon" className="ml-auto h-6 w-6" onClick={onClose} data-testid="close-summary"><X className="h-4 w-4" /></Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">{t("calculating")}</p> : (
        <>
          <p className="text-sm">{data?.summary}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">{data?.disclaimer}</p>
        </>
      )}
    </Card>
  );
}

function AreaOverview({ data }) {
  const { t } = useLang();
  const rows = [...data.areas];
  if (data.unassigned?.departments) rows.push(data.unassigned);
  return (
    <Card className="mt-4 p-4" data-testid="area-overview">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Layers className="h-4 w-4 text-accent" />{t("area_load_title")}</div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <div key={r.id || "none"} className="rounded-lg border p-3" data-testid={`area-ov-${r.id || "none"}`}>
            <div className="flex items-center gap-2">
              <span className="flex-1 font-medium">{r.name}</span>
              {r.gap > 0 ? <Badge variant="destructive">{t("missing", { n: r.gap })}</Badge> : r.required > 0 ? <StatusPill level="gron" /> : <Badge variant="secondary">{t("not_set")}</Badge>}
            </div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{t("area_meta", { deps: r.departments, req: r.required, av: r.available })}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SetupHealth() {
  const { data } = useApi("/onboarding/status");
  const { data: pricing } = useApi("/content/config/pricing");
  const { user } = useAuth();
  if (!data) return null;
  const tasks = [
    { done: (data.employees || 0) > 0, label: "Lägg till medarbetare", to: "/medarbetare" },
    { done: (data.departments || 0) > 0, label: "Skapa avdelningar", to: "/organisation" },
    { done: (data.competences || 0) > 0, label: "Lägg till kompetenser", to: "/kompetensmatris" },
    { done: (data.assignments || 0) > 0, label: "Publicera schema", to: "/schema" },
  ];
  const remaining = tasks.filter((t) => !t.done);
  const mods = user?.company_enabled_modules;
  const addon = Array.isArray(mods) ? mods.reduce((s, k) => s + (Number(pricing?.module_prices?.[k]) || 0), 0) : 0;
  const extras = (pricing?.extras || []).reduce((s, e) => s + (Number(e.price) || 0), 0);
  const total = (Number(pricing?.base_price) || 0) + addon + extras;
  const money = (n) => `${Number(n || 0).toLocaleString("sv-SE")} ${pricing?.currency || "kr"}${pricing?.period || ""}`;
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-3">
      <Card className="p-4 lg:col-span-2" data-testid="setup-health">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><ListChecks className="h-4 w-4 text-accent" />Kom igång</div>
        {remaining.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />Allt är konfigurerat – bra jobbat!</div>
        ) : (
          <div className="space-y-2">
            {remaining.map((tk) => (
              <Link key={tk.to} to={tk.to} className="flex items-center gap-2 rounded-lg border p-2.5 text-sm transition-colors hover:bg-secondary" data-testid={`health-${tk.to.slice(1)}`}>
                <Circle className="h-4 w-4 text-muted-foreground" /><span className="flex-1">{tk.label}</span><ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-4" data-testid="cost-card">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Wallet className="h-4 w-4 text-accent" />Din prisplan</div>
        {total > 0 ? (
          <>
            <div className="font-head text-2xl font-bold">{money(total)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{pricing?.base_label || "Grundpaket"} + {Array.isArray(mods) ? mods.length : 0} moduler</div>
          </>
        ) : <div className="text-sm text-muted-foreground">{pricing?.note || "Kontakta oss för en offert."}</div>}
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user, effectiveRole } = useAuth();
  const rotationOn = user?.company_rotation_enabled !== false;
  const { data, loading } = useApi("/dashboard");
  const { data: areaOv } = useApi("/staffing/area-overview");
  const { data: absence } = useApi("/hr/absence-overview");
  const [sumFor, setSumFor] = useState(null);
  const openAlerts = (absence?.items || []).filter((i) => i.flagged && !i.handled).length;
  if (loading) return <p className="text-muted-foreground">{t("loading_dash")}</p>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div>
      <PageHeader title={t("dash_title")} icon={LayoutDashboard} subtitle={t("dash_sub")} />
      <PinnedShortcuts />
      {["owner", "hr_admin"].includes(effectiveRole) && <SetupHealth />}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {rotationOn && <Stat label={t("stat_over")} value={s.over_limit} tone="danger" icon={AlertOctagon} hint={t("hint_red")} />}
        {rotationOn && <Stat label={t("stat_near")} value={s.near_limit} tone="warn" icon={AlertTriangle} hint={t("hint_yellow")} />}
        {rotationOn && <Stat label={t("stat_within")} value={s.within_limit} tone="ok" icon={CheckCircle2} hint={t("hint_green")} />}
        <Stat label={t("stat_unplanned")} value={s.unplanned_moves} tone="accent" icon={Shuffle} />
      </div>
      {openAlerts > 0 && (
        <Link to="/arbetsmiljo" data-testid="dash-absence-alert"
          className="mt-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 transition-colors hover:bg-red-100">
          <AlertOctagon className="h-6 w-6 text-red-600" />
          <div className="flex-1 font-semibold text-red-800">{t("dash_open_alerts")}</div>
          <span className="rounded-full bg-red-600 px-2.5 py-1 text-sm font-bold text-white">{openAlerts}</span>
        </Link>
      )}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2"><Bar dist={data.load_distribution} /></div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
          <Stat label={t("stat_unclassified")} value={`${s.unclassified_hours} h`} tone="warn" icon={Clock} />
          <Stat label={t("stat_employees")} value={s.employees} />
        </div>
      </div>

      {areaOv?.areas?.length > 0 && <AreaOverview data={areaOv} />}

      {sumFor && <div className="mt-4"><AiSummary key={sumFor} employeeId={sumFor} onClose={() => setSumFor(null)} /></div>}

      <Card className="mt-4 overflow-hidden">
        <div className="border-b px-4 py-3 text-sm font-semibold">{t("emp_load_title")}</div>
        {data.employees.length === 0 ? (
          <EmptyState icon={LayoutDashboard} title={t("dash_empty_title")} text={t("dash_empty_text")} />
        ) : (
          <div className="overflow-x-auto kr-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("th_employee")}</TableHead>
                  <TableHead>{t("load_latt")}</TableHead><TableHead>{t("load_medel")}</TableHead><TableHead>{t("load_tung")}</TableHead>
                  <TableHead>{t("th_limit")}</TableHead><TableHead>{t("th_coverage")}</TableHead>
                  <TableHead>{t("th_status")}</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.employees.map((e) => (
                  <TableRow key={e.employee_id} data-testid={`dash-row-${e.employee_id}`}>
                    <TableCell className="font-medium">{e.name || "—"}</TableCell>
                    <TableCell className="font-mono text-sky-700">{fPct(e.share_pct.latt)}</TableCell>
                    <TableCell className="font-mono text-amber-700">{fPct(e.share_pct.medel)}</TableCell>
                    <TableCell className="font-mono font-bold text-orange-800">{fPct(e.share_pct.tung)}</TableCell>
                    <TableCell className="font-mono">{e.rotation_limit_pct} %</TableCell>
                    <TableCell className="font-mono">{fPct(e.data_coverage_pct)}</TableCell>
                    <TableCell><StatusPill level={e.level} /></TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => setSumFor(e.employee_id)} data-testid={`summary-${e.employee_id}`}><Sparkles className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
